/**
 * Dashboard layout — pure types + small helpers. No DOM, no IO.
 *
 * A board is a list of TABS; each tab owns a set of cells placed on a grid by
 * `rect` (x, y, w, h) measured in GRID CELLS, so one widget can span many.
 * Round-trips through JSON for save / load / URL-loaded boards.
 *
 * **Grid model (v2).** A board is `minCols` × `minRows` cells that FILL the
 * window — 20 × 10 by default. The old dashboard was a 3 × 2 of exactly this
 * kind; the only thing that changed is that a widget now spans as many cells
 * as it likes instead of occupying exactly one. The old model
 * was a fixed 3 × 2 with one widget per cell, which gave a CCTV feed or a 3-D
 * viewer a sixth of the screen no matter the monitor; now a widget spans as
 * many cells as it needs and the whole board scales together, so an 8 × 4
 * widget is half the board on a laptop and half the board on the wall — the
 * cells stretch with the window, which is what keeps the board full at any
 * size.
 * See {@link computeGrid}.
 *
 * Boards also carry an optional `resources` block at the bottom (glTF-style)
 * keyed by random ids. Cells reference resources via `{ $ref: '<id>' }` placed
 * inside their config / state. Large blobs (base64 images, embedded snapshot
 * JSONs) live there so cells stay small and the file stays inspectable.
 */
import type { ResourceEntry } from '@ais/dashboard-widgets';

/** Authoring reference for the grid. Geometry is derived per-viewport from
 *  these by {@link computeGrid} — the board stores intent, not pixels. */
export interface GridSpec {
  /** Reference cell size in CSS pixels — the board's "resolution". 120 px at
   *  16 cells is a 1920-wide board; it documents the intent and drives the
   *  density control, but the RENDERED cell is whatever fits the screen. */
  cellPx: number;
  /** The board's width IN CELLS — fixed, not a floor. With `minRows` it is
   *  both the grid and the aspect ratio: 16 × 8 is a 2 : 1 canvas that scales
   *  to any screen. */
  minCols: number;
  /** The board's height in cells; see {@link minCols}. */
  minRows: number;
  /** Gutter between cells in CSS pixels. Absent = {@link DEFAULT_GAP_PX}. */
  gapPx?: number;
  /** @deprecated fraction-of-cell gutter from the fixed-canvas experiment. */
  gapPct?: number;
}

/** Where a widget sits, in CELLS. `x`/`y` are 0-based. */
export interface CellRect {
  x: number;
  y: number;
  w: number;
  h: number;
}

export interface DashboardCell {
  id: string;                   // stable cell id
  type: string;                 // widget id (e.g. 'viewer-snapshot')
  rect: CellRect;               // placement + size, in grid cells
  config: unknown;              // widget-specific config
  state?: unknown;              // last persisted widget state
  /** Operator-only cell: the read-only client view must NOT render it.
   *  Absent = visible — the flag is only written when set, so existing boards
   *  stay byte-identical. This is a data-exposure control, not cosmetics:
   *  cost tables / raw logs get authored on the same board the client sees. */
  viewerHidden?: boolean;
}

export interface DashboardTab {
  id: string;
  name: string;
  cells: DashboardCell[];
  /** Operator-only TAB — same contract as `DashboardCell.viewerHidden`, one
   *  level up, so a "Costs" or "Raw logs" tab can live on the client's board
   *  without ever being rendered to them. */
  viewerHidden?: boolean;
}

export interface DashboardLayout {
  version: 2;
  grid: GridSpec;
  tabs: DashboardTab[];
  /** The board's header wording. Absent = "Dashboard" — written only when set,
   *  so existing boards stay byte-identical. It belongs to the BOARD, not the
   *  browser, because the published board carries it: the client view's whole
   *  chrome is this one line, and "Dashboard" tells a client nothing about
   *  which site or job they are looking at. */
  title?: string;
  /** Which tab was open when the board was saved. */
  activeTabId?: string;
  /** Chrome state that belongs to the BOARD, not the browser — the same rule
   *  the viewer follows for its bars (compliance §10): collapse state
   *  serializes, it never hides in localStorage. */
  ui?: { tabsOpen?: boolean };
  /** Shared resources referenced by cells via `{ $ref }`. */
  resources?: Record<string, ResourceEntry>;
}

// --- v1 (single grid, one widget per cell) — still readable ------------------

interface LayoutV1 {
  version: 1;
  grid: { cols: number; rows: number };
  cells: Array<{
    id: string;
    type: string;
    position?: { col: number; row: number; colSpan?: number; rowSpan?: number };
    config: unknown;
    state?: unknown;
    viewerHidden?: boolean;
  }>;
  resources?: Record<string, ResourceEntry>;
}

/** The house board: 21 × 9 — the ultrawide shape control-room displays use.
 *  Cells are square, so the canvas is 21 : 9 at any size; 91 px per cell is
 *  what that comes to across a 1920-wide screen. A board may carry any other
 *  grid; this is only the default for new ones. */
/** Gutter between cells, in CSS pixels. */
export const DEFAULT_GAP_PX = 8;

export const DEFAULT_GRID: GridSpec = { cellPx: 96, minCols: 20, minRows: 10, gapPx: DEFAULT_GAP_PX };

/** Recognise any board file we can load (v1 or v2). */
export function isLayoutJson(json: unknown): json is DashboardLayout | LayoutV1 {
  if (!json || typeof json !== 'object') return false;
  const o = json as Record<string, unknown>;
  if (o.version === 2) return Array.isArray(o.tabs);
  if (o.version === 1) return Array.isArray(o.cells);
  return false;
}

/**
 * Resolve the grid for a viewport.
 *
 * The grid is `minCols` × `minRows` and its tracks are FRACTIONS of the space,
 * so the board always fills the window exactly — the behaviour the dashboard
 * had before widgets could span cells, and the reason it never had dead bands
 * or a scrollbar. A cell is therefore not square, and does not need to be: a
 * widget is a box of content, and 8 × 5 means "40% of the width by 50% of the
 * height" on every screen.
 *
 * What this deliberately does NOT do: preserve an aspect ratio (that
 * letterboxes), derive the count from the screen (that made the row count
 * drift), or cap the rows (nothing is ever clipped — the count is exactly what
 * the board asks for).
 */
export function computeGrid(
  availW: number,
  availH: number,
  spec: GridSpec = DEFAULT_GRID,
): { cols: number; rows: number; colW: number; rowH: number; gap: number } {
  const cols = Math.max(1, Math.round(spec.minCols));
  const rows = Math.max(1, Math.round(spec.minRows));
  const gap = Math.max(0, Math.round(spec.gapPx ?? DEFAULT_GAP_PX));
  const colW = Math.max(1, (Math.max(0, availW) - gap * (cols - 1)) / cols);
  const rowH = Math.max(1, (Math.max(0, availH) - gap * (rows - 1)) / rows);
  return { cols, rows, colW, rowH, gap };
}

/** Clamp a rect into a grid for RENDERING — never mutate the stored rect. A
 *  board authored on a 21-column monitor must still show every widget on a
 *  16-column one, so an out-of-range cell is pulled in rather than clipped. */
export function clampRect(rect: CellRect, cols: number, rows: number): CellRect {
  const w = Math.max(1, Math.min(rect.w, cols));
  const h = Math.max(1, Math.min(rect.h, rows));
  const x = Math.max(0, Math.min(rect.x, cols - w));
  const y = Math.max(0, Math.min(rect.y, rows - h));
  return { x, y, w, h };
}

/** Do two rects overlap? Placement refuses overlap rather than reflowing the
 *  board under the operator (a widget stays where it was dropped). */
export function rectsOverlap(a: CellRect, b: CellRect): boolean {
  return a.x < b.x + b.w && b.x < a.x + a.w && a.y < b.y + b.h && b.y < a.y + a.h;
}

/** Is `rect` free in `tab`, ignoring the cell being moved (`selfId`)? */
export function rectIsFree(tab: DashboardTab, rect: CellRect, selfId?: string): boolean {
  return !tab.cells.some((c) => c.id !== selfId && rectsOverlap(c.rect, rect));
}

/**
 * First free slot for a `w`×`h` widget, scanning row-major. Returns null when
 * the tab is full at this size — the caller then tells the operator instead of
 * stacking widgets invisibly on top of each other.
 */
export function findFreeRect(
  tab: DashboardTab, w: number, h: number, cols: number, rows: number,
): CellRect | null {
  for (let y = 0; y + h <= rows; y++) {
    for (let x = 0; x + w <= cols; x++) {
      const rect = { x, y, w, h };
      if (rectIsFree(tab, rect)) return rect;
    }
  }
  return null;
}

export function newCellId(prefix = 'cell'): string {
  return `${prefix}-${Math.random().toString(36).slice(2, 8)}`;
}

export function newTabId(): string {
  return `tab-${Math.random().toString(36).slice(2, 8)}`;
}

export function newResourceId(): string {
  return `res-${Math.random().toString(36).slice(2, 10)}`;
}

export function newTab(name: string, cells: DashboardCell[] = []): DashboardTab {
  return { id: newTabId(), name, cells };
}

/** A copy of `tabs` with the tab at `from` moved to index `to` (clamped). The
 *  array order IS the rail order, so this is the whole of a tab reorder. An
 *  out-of-range `from` returns an unchanged copy. */
export function moveTab<T>(tabs: readonly T[], from: number, to: number): T[] {
  const out = tabs.slice();
  if (!Number.isInteger(from) || from < 0 || from >= out.length) return out;
  const [tab] = out.splice(from, 1);
  const at = Math.max(0, Math.min(out.length, Math.trunc(Number.isFinite(to) ? to : out.length)));
  out.splice(at, 0, tab);
  return out;
}

/** The out-of-the-box board: the six viewer cells the dashboard always had, as
 *  even blocks on the 20 × 10 canvas — 3 across, 2 down, filling it exactly. */
export function defaultLayout(): DashboardLayout {
  const cells: DashboardCell[] = [];
  // 20 columns don't divide by 3, so the middle column carries the extra cell
  // (7 / 6 / 7) — even-looking, and no dead strip down the right-hand side.
  const widths = [7, 6, 7];
  const xs = [0, 7, 13];
  for (let i = 0; i < 6; i++) {
    const col = i % 3;
    cells.push({
      id: `cell-${i}`,
      type: 'viewer-snapshot',
      rect: { x: xs[col], y: i < 3 ? 0 : 5, w: widths[col], h: 5 },
      config: {},
    });
  }
  const tab = newTab('Main', cells);
  return { version: 2, grid: { ...DEFAULT_GRID }, tabs: [tab], activeTabId: tab.id };
}

/**
 * Bring any accepted board file up to v2.
 *
 * A v1 board is one grid of same-sized cells, so it becomes ONE tab and each
 * cell's rect is derived from its old `position` (1-based col/row + optional
 * spans). Cells that never carried a position were laid out by source order in
 * a `cols`-wide flow — reproduce exactly that, then scale the whole thing so a
 * 3×2 board still fills a 16×8 grid instead of hiding in the top-left corner.
 * Unknown widget types keep their config/state untouched, as ever.
 */
export function migrateLayout(json: DashboardLayout | LayoutV1): DashboardLayout {
  if ((json as DashboardLayout).version === 2) {
    const v2 = json as DashboardLayout;
    const tabs = (v2.tabs ?? []).map((t) => ({
      ...t,
      cells: (t.cells ?? []).map((c) => ({ ...c, rect: { ...(c.rect ?? { x: 0, y: 0, w: 4, h: 3 }) } })),
    }));
    return {
      version: 2,
      grid: { ...DEFAULT_GRID, ...(v2.grid ?? {}) },
      tabs: tabs.length ? tabs : [newTab('Main')],
      activeTabId: v2.activeTabId ?? tabs[0]?.id,
      ...(typeof v2.title === 'string' && v2.title.trim() ? { title: v2.title.trim() } : {}),
      ...(v2.ui ? { ui: { ...v2.ui } } : {}),
      ...(v2.resources ? { resources: v2.resources } : {}),
    };
  }

  const v1 = json as LayoutV1;
  const cols = Math.max(1, v1.grid?.cols ?? 3);
  const rows = Math.max(1, v1.grid?.rows ?? 2);
  // Scale the old grid onto the v2 floor so the board keeps its proportions.
  const sx = Math.max(1, Math.floor(DEFAULT_GRID.minCols / cols));
  const sy = Math.max(1, Math.floor(DEFAULT_GRID.minRows / rows));
  const cells: DashboardCell[] = (v1.cells ?? []).map((c, i) => {
    const p = c.position;
    const col = (p ? p.col - 1 : i % cols);
    const row = (p ? p.row - 1 : Math.floor(i / cols));
    const rect: CellRect = {
      x: Math.max(0, col) * sx,
      y: Math.max(0, row) * sy,
      w: Math.max(1, p?.colSpan ?? 1) * sx,
      h: Math.max(1, p?.rowSpan ?? 1) * sy,
    };
    const out: DashboardCell = { id: c.id, type: c.type, rect, config: c.config };
    if (c.state !== undefined) out.state = c.state;
    if (c.viewerHidden) out.viewerHidden = true;
    return out;
  });
  const tab = newTab('Main', cells);
  return {
    version: 2,
    grid: { ...DEFAULT_GRID },
    tabs: [tab],
    activeTabId: tab.id,
    ...(v1.resources ? { resources: v1.resources } : {}),
  };
}

/**
 * Walk an arbitrary JSON-like value and collect every `$ref` string it
 * encounters in `{ $ref: '...' }` shapes. Used to GC orphan resources at save.
 */
export function collectResourceRefs(node: unknown, out: Set<string>): void {
  if (node == null) return;
  if (typeof node !== 'object') return;
  if (Array.isArray(node)) {
    for (const v of node) collectResourceRefs(v, out);
    return;
  }
  const o = node as Record<string, unknown>;
  if (typeof o.$ref === 'string') out.add(o.$ref);
  for (const v of Object.values(o)) collectResourceRefs(v, out);
}

/**
 * Produce a fresh layout object with `resources` placed last (so it shows up at
 * the bottom of the JSON, glTF-style) and orphan resources GC'd out. Walks
 * EVERY tab — a resource referenced only from a background tab must survive.
 */
export function serialiseLayout(layout: DashboardLayout): DashboardLayout {
  const refs = new Set<string>();
  for (const tab of layout.tabs) {
    for (const cell of tab.cells) {
      collectResourceRefs(cell.config, refs);
      collectResourceRefs(cell.state, refs);
    }
  }
  const kept: Record<string, ResourceEntry> = {};
  if (layout.resources) {
    for (const id of refs) {
      const entry = layout.resources[id];
      if (entry) kept[id] = entry;
    }
  }
  const out: DashboardLayout = {
    version: 2,
    grid: layout.grid,
    tabs: layout.tabs,
    ...(layout.activeTabId ? { activeTabId: layout.activeTabId } : {}),
    ...(layout.title ? { title: layout.title } : {}),
    ...(layout.ui ? { ui: layout.ui } : {}),
  };
  if (Object.keys(kept).length > 0) out.resources = kept;
  return out;
}
